import React, { useState, useEffect } from 'react';
import axios from 'axios';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import SignInRequired from '../components/SignInRequired';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomMealsPage = () => {
  const [myMeals, setMyMeals] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [editingMeal, setEditingMeal] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Close modal function
  const closeModal = () => {
    setSelectedMeal(null);
    setEditingMeal(null);
    document.body.style.overflow = 'auto';
  };
  
  // Open view modal
  const openViewModal = (meal) => {
    setSelectedMeal(meal);
    document.body.style.overflow = 'hidden';
  };
  
  // Open edit form
  const openEditForm = (meal) => {
    setEditingMeal(meal);
    document.body.style.overflow = 'hidden';
  };

  useEffect(() => {
    fetchMyMeals();
  }, []);

  const fetchMyMeals = async () => {
    try {
      const response = await axios.get(`${API}/custom-meals/my-meals`);
      setMyMeals(response.data);
    } catch (error) {
      console.error('Error fetching meals:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleMealUpdated = () => {
    fetchMyMeals();
    setEditingMeal(null);
  };

  const deleteMeal = async (mealId) => {
    if (!window.confirm('Delete this meal?')) return;
    try {
      await axios.delete(`${API}/custom-meals/${mealId}`);
      fetchMyMeals();
    } catch (error) {
      alert('Failed to delete meal');
    }
  };

  return (
    <SignInRequired featureName="Custom Meals">
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h4 className="heading-4" style={{ color: 'rgb(24, 24, 24)' }}>🍲 My Meals</h4>
              <p className="body-medium" style={{ color: 'rgb(112, 112, 112)' }}>Create and manage your personalized meals</p>
            </div>
            <button onClick={() => setShowCreateForm(true)} className="btn-primary">➕ Create New Meal</button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
              <p className="body-medium" style={{ color: 'rgb(112, 112, 112)' }}>Loading...</p>
            </div>
          ) : myMeals.length === 0 ? (
            <div className="text-center py-12">
              <p className="body-large mb-4" style={{ color: 'rgb(112, 112, 112)' }}>No custom meals yet</p>
              <p className="body-medium mb-6" style={{ color: 'rgb(143, 143, 143)' }}>Create your first custom meal to start building your personal food library</p>
              <button onClick={() => setShowCreateForm(true)} className="btn-primary">🍽️ Create Your First Meal</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myMeals.map((meal) => (
                <div 
                  key={meal.id} 
                  className="p-4 rounded-lg border hover:shadow-md transition-shadow cursor-pointer" 
                  style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}
                  onClick={() => openViewModal(meal)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="body-medium font-semibold" style={{ color: 'rgb(24, 24, 24)' }}>{meal.name}</h3>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMeal(meal.id);
                      }} 
                      className="text-red-500 hover:text-red-700 text-xl"
                    >
                      ×
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div><span style={{ color: 'rgb(112, 112, 112)' }}>Calories:</span> <strong>{meal.calories}</strong></div>
                    <div><span style={{ color: 'rgb(112, 112, 112)' }}>Protein:</span> <strong>{meal.protein}g</strong></div>
                    <div><span style={{ color: 'rgb(112, 112, 112)' }}>Carbs:</span> <strong>{meal.carbs}g</strong></div>
                    <div><span style={{ color: 'rgb(112, 112, 112)' }}>Fat:</span> <strong>{meal.fat}g</strong></div>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs" style={{ color: 'rgb(143, 143, 143)' }}>{meal.type} • {meal.region} • {meal.category}</p>
                    <div className="flex gap-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          openViewModal(meal);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                        title="View recipe"
                      >
                        👁️ View
                      </button>
                      {!meal.source && !meal.video_id && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditForm(meal);
                          }}
                          className="text-sm text-green-600 hover:text-green-800 flex items-center"
                          title="Edit meal"
                        >
                          ✏️ Edit
                        </button>
                      )}
                    </div>
                  </div>
                  {meal.is_public && (
                    <span className="text-xs px-2 py-1 rounded mt-2 inline-block" style={{ backgroundColor: 'rgba(0, 128, 255, 0.1)', color: 'rgb(0, 128, 255)' }}>
                      🌐 Shared with community
                    </span>
                  )}
                  {meal.recipe && (
                    <p className="text-xs mt-2 line-clamp-2" style={{ color: 'rgb(112, 112, 112)' }}>{meal.recipe}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreateForm && (
        <CreateMealForm 
          onClose={() => {
            setShowCreateForm(false);
            fetchMyMeals();
          }} 
        />
      )}
      
      {editingMeal && (
        <CreateMealForm 
          mealToEdit={editingMeal}
          onClose={() => setEditingMeal(null)}
          onUpdate={handleMealUpdated}
        />
      )}
      
      {/* Recipe Modal */}
      {selectedMeal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold">{selectedMeal.name}</h2>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                  <CloseIcon />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">Nutrition Information</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-gray-600">Calories:</span> <strong>{selectedMeal.calories}</strong></div>
                    <div><span className="text-gray-600">Protein:</span> <strong>{selectedMeal.protein}g</strong></div>
                    <div><span className="text-gray-600">Carbs:</span> <strong>{selectedMeal.carbs}g</strong></div>
                    <div><span className="text-gray-600">Fat:</span> <strong>{selectedMeal.fat}g</strong></div>
                    {selectedMeal.servings && (
                      <div className="col-span-2"><span className="text-gray-600">Servings:</span> <strong>{selectedMeal.servings}</strong></div>
                    )}
                  </div>
                </div>
                
                {selectedMeal.video_id && (
                  <div className="md:col-span-2">
                    <h3 className="font-semibold text-lg mb-2">Source</h3>
                    <a 
                      href={`https://www.youtube.com/watch?v=${selectedMeal.video_id}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 flex items-center"
                    >
                      <OpenInNewIcon fontSize="small" className="mr-1" /> Watch on YouTube
                    </a>
                  </div>
                )}
              </div>
              
              {selectedMeal.description && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">Description</h3>
                  <p className="text-gray-700">{selectedMeal.description}</p>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-6">
                {selectedMeal.ingredients && selectedMeal.ingredients.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Ingredients</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      {selectedMeal.ingredients.map((ingredient, index) => (
                        <li key={index} className="text-gray-700">{ingredient}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedMeal.instructions && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Instructions</h3>
                    {Array.isArray(selectedMeal.instructions) ? (
                      <ol className="list-decimal pl-5 space-y-2">
                        {selectedMeal.instructions.map((step, index) => (
                          <li key={index} className="text-gray-700">{step}</li>
                        ))}
                      </ol>
                    ) : (
                      <div className="whitespace-pre-line text-gray-700">{selectedMeal.instructions}</div>
                    )}
                  </div>
                )}
              </div>
              
              {selectedMeal.tags && selectedMeal.tags.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex flex-wrap gap-2">
                    {selectedMeal.tags.map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </SignInRequired>
  );
};

const CreateMealForm = ({ onClose, mealToEdit, onUpdate }) => {
  const [formData, setFormData] = useState(mealToEdit ? {
    ...mealToEdit,
    ingredients: mealToEdit.ingredients ? mealToEdit.ingredients.join(', ') : '',
    recipe: mealToEdit.recipe || mealToEdit.instructions || ''
  } : {
    name: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    category: 'main_course',
    meal_type: 'lunch',
    type: 'veg',
    region: 'North',
    ingredients: '',
    recipe: '',
    is_public: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        calories: parseFloat(formData.calories),
        protein: parseFloat(formData.protein),
        carbs: parseFloat(formData.carbs),
        fat: parseFloat(formData.fat),
        ingredients: formData.ingredients.split(',').map(i => i.trim()).filter(i => i)
      };

      if (mealToEdit) {
        await axios.put(`${API}/custom-meals/${mealToEdit.id}`, payload);
        alert('✅ Meal updated successfully!');
        onUpdate();
      } else {
        await axios.post(`${API}/custom-meals/create`, payload);
        alert('✅ Meal created successfully!');
        onClose();
      }
    } catch (error) {
      alert('Failed to create meal: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="heading-3" style={{ color: 'rgb(24, 24, 24)' }}>{mealToEdit ? 'Edit' : 'Create'} Custom Meal</h2>
          <button onClick={onClose} className="text-2xl" style={{ color: 'rgb(112, 112, 112)' }}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Meal Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              placeholder="e.g., My Special Dal"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Calories *</label>
              <input
                type="number"
                value={formData.calories}
                onChange={(e) => setFormData({...formData, calories: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                required
              />
            </div>
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Protein (g) *</label>
              <input
                type="number"
                value={formData.protein}
                onChange={(e) => setFormData({...formData, protein: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                required
              />
            </div>
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Carbs (g) *</label>
              <input
                type="number"
                value={formData.carbs}
                onChange={(e) => setFormData({...formData, carbs: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                required
              />
            </div>
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Fat (g) *</label>
              <input
                type="number"
                value={formData.fat}
                onChange={(e) => setFormData({...formData, fat: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              >
                <option value="main_course">Main Course</option>
                <option value="dal">Dal</option>
                <option value="rice">Rice</option>
                <option value="bread">Bread</option>
                <option value="snack">Snack</option>
                <option value="dessert">Dessert</option>
              </select>
            </div>
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Meal Type</label>
              <select
                value={formData.meal_type}
                onChange={(e) => setFormData({...formData, meal_type: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              >
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Food Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              >
                <option value="veg">Vegetarian</option>
                <option value="non-veg">Non-Veg</option>
                <option value="vegan">Vegan</option>
              </select>
            </div>
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Region</label>
              <select
                value={formData.region}
                onChange={(e) => setFormData({...formData, region: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              >
                <option value="North">North Indian</option>
                <option value="South">South Indian</option>
                <option value="East">East Indian</option>
                <option value="West">West Indian</option>
                <option value="All">All Regions</option>
              </select>
            </div>
          </div>

          <div>
            <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Ingredients (comma separated)</label>
            <input
              type="text"
              value={formData.ingredients}
              onChange={(e) => setFormData({...formData, ingredients: e.target.value})}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              placeholder="e.g., Dal, Onions, Tomatoes, Spices"
            />
          </div>

          <div>
            <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Recipe (optional)</label>
            <textarea
              value={formData.recipe || formData.instructions || ''}
              onChange={(e) => setFormData({...formData, recipe: e.target.value})}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              rows="3"
              placeholder="Cooking instructions..."
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_public"
              checked={formData.is_public}
              onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
              className="w-4 h-4"
            />
            <label htmlFor="is_public" className="body-medium" style={{ color: 'rgb(24, 24, 24)' }}>
              Share with community
            </label>
          </div>

          <div className="flex gap-2">
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading 
                ? (mealToEdit ? 'Updating...' : 'Creating...') 
                : `✅ ${mealToEdit ? 'Update' : 'Create'} Meal`}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomMealsPage;